/**
 * NotificarPagoProveedorModal
 *
 * Se abre automáticamente tras aplicar un pago en CxP (tanto desde
 * "Facturas por Pagar" como desde "Por Proveedor"). Permite editar
 * destinatarios, asunto y mensaje, y envía la notificación con el
 * comprobante adjunto. Persiste los destinatarios por proveedor para
 * uso futuro.
 *
 * Layout: 2 columnas (form izquierda, vista previa derecha) — replica
 * el HTML que se envía en el correo.
 */

import { useState, useEffect } from 'react';
import { Send, X, Loader, Save } from 'lucide-react';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import { gastosApi } from '../utils/api';

const COLOR = '#2563eb';

function fmtMoney(v, mon) {
  const n = parseFloat(v) || 0;
  return (mon === 'USD' ? '$' : '₡') + n.toLocaleString(
    mon === 'USD' ? 'en-US' : 'es-CR',
    { minimumFractionDigits: 2, maximumFractionDigits: 2 }
  );
}

export default function NotificarPagoProveedorModal({ info, emisor, onClose }) {
  const emisorNombre = emisor?.nombre || emisor?.nombreComercial || 'Finance 360';
  const provNombreInicial = info?.proveedorNombre || '';

  // Toggle idioma ES/EN (persistido por proveedor)
  const idiomaKey = `f360_pago_prov_idioma_${info?.proveedorId || 'def'}`;
  const [idioma, setIdiomaState] = useState(() => localStorage.getItem(idiomaKey) || 'es');

  const PLANTILLAS = {
    es: {
      subject: `Notificación de pago — ${emisorNombre}`,
      mensaje: `Estimados {proveedor},\n\nLes informamos que hemos realizado el pago de su(s) factura(s) por un monto total de {monto} con fecha {fecha}. En el detalle adjunto encontrarán las facturas cubiertas y el comprobante respectivo.\n\nQuedamos atentos a cualquier consulta.\n\nSaludos cordiales,\n{empresa}`,
      labels: {
        titulo: 'NOTIFICACIÓN DE PAGO',
        factura: 'Factura', total: 'Total', aplicado: 'Aplicado', saldo: 'Saldo',
        montoTotal: 'Monto total pagado:',
        fechaPago: 'Fecha de pago:', moneda: 'Moneda:', medioPago: 'Medio de pago:',
        banco: 'Banco:', numComprobante: 'Núm. comprobante:'
      }
    },
    en: {
      subject: `Payment notification — ${emisorNombre}`,
      mensaje: `Dear {proveedor},\n\nWe are pleased to inform you that we have processed the payment of your invoice(s) in the total amount of {monto} on {fecha}. The attached detail contains the invoices covered and the corresponding receipt.\n\nPlease feel free to reach out with any questions.\n\nBest regards,\n{empresa}`,
      labels: {
        titulo: 'PAYMENT NOTIFICATION',
        factura: 'Invoice', total: 'Total', aplicado: 'Applied', saldo: 'Balance',
        montoTotal: 'Total amount paid:',
        fechaPago: 'Payment date:', moneda: 'Currency:', medioPago: 'Payment method:',
        banco: 'Bank:', numComprobante: 'Receipt #:'
      }
    }
  };

  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [subject, setSubject] = useState(PLANTILLAS[idioma].subject);
  const [mensaje, setMensaje] = useState(PLANTILLAS[idioma].mensaje);
  const labels = PLANTILLAS[idioma].labels;

  // Plantillas guardadas en BD (por idioma) — fallback a default si no hay
  const [plantillasGuardadas, setPlantillasGuardadas] = useState({ es: null, en: null });
  const [saving, setSaving] = useState(false);

  const setIdioma = (nuevo) => {
    setIdiomaState(nuevo);
    localStorage.setItem(idiomaKey, nuevo);
    setSubject(PLANTILLAS[nuevo].subject);
    setMensaje(plantillasGuardadas[nuevo] || PLANTILLAS[nuevo].mensaje);
  };
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  // Detalle real desde el backend (para mostrar las FE con monto aplicado real)
  const [detalle, setDetalle] = useState(null); // { proveedor, parent, pagos: [...] }

  useEffect(() => {
    (async () => {
      // Destinatarios guardados
      try {
        if (info.proveedorId) {
          const res = await gastosApi.getProveedorNotifDestinatarios(info.proveedorId);
          const d = res.data?.data || {};
          setTo(d.recipientEmails || d.proveedorEmail || '');
          if (d.ccEmails && d.ccEmails.trim()) { setCc(d.ccEmails); setShowCc(true); }
          if (d.bccEmails && d.bccEmails.trim()) { setBcc(d.bccEmails); setShowBcc(true); }
        }
      } catch (e) { /* sin lista guardada */ }

      // Detalle del pago (para la vista previa)
      try {
        const params = info.parentId ? { parentId: info.parentId } : { pagoId: info.pagoId };
        const res = await gastosApi.getCxpPagoDetalle(params);
        if (res.data?.success) setDetalle(res.data.data);
      } catch (e) { /* preview cae a info básica */ }

      // Plantillas guardadas (por idioma) — si existen, sobreescriben la default del idioma activo
      try {
        const res = await gastosApi.getCxpNotifConfig();
        const d = res.data?.data || {};
        const guardadas = { es: d.mensajePlantillaEs || null, en: d.mensajePlantillaEn || null };
        setPlantillasGuardadas(guardadas);
        const actual = guardadas[idioma];
        if (actual) setMensaje(actual);
      } catch (e) { /* sin plantilla guardada */ }

      setLoading(false);
    })();
  }, [info.proveedorId, info.parentId, info.pagoId]);

  const guardarPlantilla = async () => {
    setSaving(true);
    try {
      const payload = idioma === 'en'
        ? { mensajePlantillaEn: mensaje }
        : { mensajePlantillaEs: mensaje };
      await gastosApi.saveCxpNotifConfig(payload);
      setPlantillasGuardadas(prev => ({ ...prev, [idioma]: mensaje }));
      toast.success(`Plantilla ${idioma === 'en' ? 'inglés' : 'español'} guardada`);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al guardar plantilla');
    } finally {
      setSaving(false);
    }
  };

  // Valores efectivos: merge — detalle real prioriza, info props rellena nulls
  const proveedor = detalle?.proveedor || { nombre: provNombreInicial };
  const dp = detalle?.parent || {};
  const parent = {
    fechaPago: dp.fechaPago || info.fechaPago,
    montoTotal: (dp.montoTotal !== undefined && dp.montoTotal !== null) ? dp.montoTotal : info.montoTotal,
    moneda: dp.moneda || info.moneda,
    medioPago: dp.medioPago || info.medioPago,
    banco: dp.banco || info.banco,
    numeroComprobante: dp.numeroComprobante || info.numeroComprobante,
    comprobanteFilename: dp.comprobanteFilename || info.comprobanteFilename
  };
  const pagos = detalle?.pagos || [];

  const recipientsCount = to.split(/[,;\s]+/).map(s => s.trim()).filter(s => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s)).length;

  const aplicarVars = (txt) => (txt || '')
    .replace(/\{proveedor\}/g, proveedor.nombre || '')
    .replace(/\{empresa\}/g, emisorNombre)
    .replace(/\{monto\}/g, fmtMoney(parent.montoTotal, parent.moneda))
    .replace(/\{fecha\}/g, dayjs(parent.fechaPago).format(idioma === 'en' ? 'MM/DD/YYYY' : 'DD/MM/YYYY'));

  // Vista previa real: la arma el servidor igual que el envío (diseño Finance360)
  const [previewHtml, setPreviewHtml] = useState('');
  useEffect(() => {
    if (!info?.parentId && !info?.pagoId) return undefined;
    let cancelado = false;
    const t = setTimeout(async () => {
      try {
        const r = await gastosApi.previewNotificacionPagoProveedor({ parentId: info.parentId, pagoId: info.pagoId, mensaje, idioma });
        if (!cancelado) setPreviewHtml(r.data?.html || '');
      } catch {
        if (!cancelado) setPreviewHtml('<p style="font-family:sans-serif;color:#94a3b8;padding:24px;">No se pudo cargar la vista previa.</p>');
      }
    }, 400);
    return () => { cancelado = true; clearTimeout(t); };
  }, [info?.parentId, info?.pagoId, mensaje, idioma]);

  const handleSend = async () => {
    if (recipientsCount === 0) { toast.error('Agregá al menos un destinatario válido'); return; }
    setSending(true);
    try {
      const res = await gastosApi.enviarNotificacionPagoProveedor({
        parentId: info.parentId,
        pagoId: info.pagoId,
        recipients: to,
        cc,
        bcc,
        subject,
        mensaje,
        idioma
      });
      const facts = res.data?.data?.facturasIncluidas || info.facturasCount;
      const adj = res.data?.data?.comprobanteAdjuntado;
      toast.success(`Notificación enviada · ${facts} factura(s)${adj ? ' · comprobante adjunto' : ''}`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al enviar la notificación');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl mx-4 max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b-[3px] border-b-blue-500 rounded-t-xl shrink-0">
          <div className="flex items-center gap-4">
            <h3 className="font-semibold text-gray-900 flex items-center gap-3 text-lg">
              <span className="w-9 h-9 rounded-xl border border-indigo-300 flex items-center justify-center"><Send className="h-4 w-4 text-indigo-600" /></span>
              Notificar Pago al Proveedor
            </h3>
            {/* Toggle idioma ES/EN */}
            <div className="inline-flex rounded-lg p-0.5 gap-0.5 border border-gray-200">
              <button
                onClick={() => setIdioma('es')}
                className={`px-3 py-1.5 rounded text-xs font-semibold transition-all flex items-center gap-1.5 ${
                  idioma === 'es' ? 'border border-indigo-400 text-indigo-700' : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                <span className="text-base leading-none">🇨🇷</span> Español
              </button>
              <button
                onClick={() => setIdioma('en')}
                className={`px-3 py-1.5 rounded text-xs font-semibold transition-all flex items-center gap-1.5 ${
                  idioma === 'en' ? 'border border-indigo-400 text-indigo-700' : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                <span className="text-base leading-none">🇺🇸</span> English
              </button>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 lg:divide-x lg:divide-gray-100">

            {/* IZQUIERDA: form */}
            <div className="px-6 py-5 space-y-3">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Destinatarios <span className="text-xs text-gray-400 font-normal">(separados por coma)</span>
                  </label>
                  <div className="flex items-center gap-2">
                    {!showCc && (
                      <button type="button" onClick={() => setShowCc(true)} className="text-[11px] font-medium text-blue-600 hover:text-blue-800 hover:underline">+ CC</button>
                    )}
                    {!showBcc && (
                      <button type="button" onClick={() => setShowBcc(true)} className="text-[11px] font-medium text-blue-600 hover:text-blue-800 hover:underline">+ CCO</button>
                    )}
                  </div>
                </div>
                <textarea value={to} onChange={(e) => setTo(e.target.value)} rows={2}
                  placeholder="contabilidad@proveedor.com, tesoreria@proveedor.com" disabled={loading}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 resize-none" />
                <p className="text-[11px] text-gray-400 mt-1">
                  {loading ? 'Cargando destinatarios guardados…' : `${recipientsCount} destinatario${recipientsCount !== 1 ? 's' : ''} válido${recipientsCount !== 1 ? 's' : ''}`} · Se guardan al enviar para uso futuro con este proveedor.
                </p>
              </div>

              {showCc && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">CC <span className="text-xs text-gray-400 font-normal">(en copia)</span></label>
                    <button type="button" onClick={() => { setCc(''); setShowCc(false); }} className="text-[11px] text-gray-400 hover:text-red-500">Quitar</button>
                  </div>
                  <input type="email" value={cc} onChange={(e) => setCc(e.target.value)} placeholder="copia@ejemplo.com"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400" />
                </div>
              )}

              {showBcc && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">CCO <span className="text-xs text-gray-400 font-normal">(copia oculta)</span></label>
                    <button type="button" onClick={() => { setBcc(''); setShowBcc(false); }} className="text-[11px] text-gray-400 hover:text-red-500">Quitar</button>
                  </div>
                  <input type="email" value={bcc} onChange={(e) => setBcc(e.target.value)} placeholder="copia.oculta@ejemplo.com"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400" />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Asunto</label>
                <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400" />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Mensaje <span className="text-[11px] text-gray-400 font-normal">· plantilla {idioma === 'en' ? '🇺🇸 EN' : '🇨🇷 ES'}</span>
                  </label>
                  <button
                    type="button"
                    onClick={guardarPlantilla}
                    disabled={saving || loading}
                    className="flex items-center gap-1 text-[11px] font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-2 py-1 rounded transition disabled:opacity-50"
                    title={`Guardar este texto como plantilla por defecto para ${idioma === 'en' ? 'inglés' : 'español'} (se carga la próxima vez)`}
                  >
                    {saving ? <Loader className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                    Guardar como predeterminado
                  </button>
                </div>
                <textarea value={mensaje} onChange={(e) => setMensaje(e.target.value)} rows={8}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 resize-none leading-relaxed" />
                <p className="text-[11px] text-gray-400 mt-1">
                  Variables: <code className="bg-gray-100 px-1 rounded">{'{proveedor}'}</code>, <code className="bg-gray-100 px-1 rounded">{'{empresa}'}</code>, <code className="bg-gray-100 px-1 rounded">{'{monto}'}</code>, <code className="bg-gray-100 px-1 rounded">{'{fecha}'}</code> — se reemplazan al enviar.
                </p>
              </div>

              {parent.comprobanteFilename && (
                <div className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 flex items-center gap-2">
                  📎 Comprobante <strong>{parent.comprobanteFilename}</strong> se adjunta al correo.
                </div>
              )}
            </div>

            {/* DERECHA: vista previa profesional (replica el HTML que se envía) */}
            <div className="px-6 py-5 bg-gray-50/40">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">
                Vista previa del correo {pagos.length > 0 && <span className="normal-case text-gray-500">· {pagos.length} factura{pagos.length !== 1 ? 's' : ''}</span>}
              </p>

              <div className="border border-gray-200 rounded-lg overflow-hidden bg-[#EFF1F7]">
                {loading && !previewHtml
                  ? <div className="py-16 text-center text-sm text-gray-500"><Loader className="h-5 w-5 animate-spin inline" /> Cargando vista previa…</div>
                  : <iframe title="Vista previa del correo" srcDoc={previewHtml} className="w-full h-[680px] border-0" />}
              </div>
            </div>

          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-xl shrink-0">
          <button onClick={onClose} className="px-5 py-2 border-2 border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium">
            No enviar
          </button>
          <button onClick={handleSend} disabled={sending || loading || recipientsCount === 0}
            className="px-6 py-2 border-2 border-blue-500 text-blue-600 rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-50 hover:bg-blue-50">
            {sending ? <Loader className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Enviar Notificación
          </button>
        </div>
      </div>
    </div>
  );
}
